import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Environment, useGLTF, useTexture } from '@react-three/drei';
import { EffectComposer, Bloom, LUT } from '@react-three/postprocessing';
import * as THREE from 'three';
import { trackSceneInteraction } from '../analytics';

const MODEL_PATH = '/3d/LeePerrySmith/LeePerrySmithHead8.glb';
const HDR_PATH = '/3d/LeePerrySmith/photo_studio_loft_hall_256.hdr';
const LUT_PATH = '/3d/LeePerrySmith/SunshinelLUT.png';
const FACE_VIDEO_PATH = '/video/SpaceFace.mp4';

const ARHeadScene = () => {
  const { scene } = useGLTF(MODEL_PATH);
  const lut = useTexture(LUT_PATH);
  const faceVideo = useMemo(() => {
    const video = document.createElement('video');
    video.src = FACE_VIDEO_PATH;
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    return video;
  }, []);
  const faceTexture = useMemo(() => new THREE.VideoTexture(faceVideo), [faceVideo]);
  const [isDragging, setIsDragging] = useState(false);
  const lastPoint = useRef({ x: 0, y: 0 });
  const boneRef = useRef(null);
  const faceMaterialRef = useRef(null);
  const autoAnimRef = useRef({
    isAnimating: false,
    startTime: 0,
    duration: 1200,
    startRotation: new THREE.Euler(),
    targetRotation: new THREE.Euler(),
  });
  const rafRef = useRef(null);
  const hasTrackedInteraction = useRef(false);

  const trackFirstInteraction = useCallback(() => {
    if (!hasTrackedInteraction.current) {
      hasTrackedInteraction.current = true;
      trackSceneInteraction('ARHeadScene', 'interact');
    }
  }, []);

  useEffect(() => {
    if (!lut) {
      return;
    }
    lut.flipY = false;
    lut.generateMipmaps = false;
    lut.needsUpdate = true;
  }, [lut]);

  useEffect(() => {
    faceTexture.flipY = false;
    faceTexture.colorSpace = THREE.SRGBColorSpace;
    faceTexture.needsUpdate = true;

    // Ensure video src is set (may have been cleared by StrictMode cleanup)
    if (!faceVideo.src || faceVideo.src === '') {
      faceVideo.src = FACE_VIDEO_PATH;
    }

    let disposed = false;
    let interactListener = null;

    const tryPlay = () => {
      if (disposed) return;
      const playPromise = faceVideo.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          if (disposed) return;
          // Autoplay blocked — retry on next user interaction
          if (!interactListener) {
            interactListener = () => {
              faceVideo.play().catch(() => undefined);
            };
            document.addEventListener('pointerdown', interactListener, { once: true });
            document.addEventListener('touchstart', interactListener, { once: true });
          }
        });
      }
    };

    // Try to play immediately if the video has enough data, otherwise
    // listen for multiple readiness events to avoid the race where
    // canplaythrough fires before the listener is attached.
    const onReady = () => tryPlay();

    if (faceVideo.readyState >= 2) {
      // HAVE_CURRENT_DATA or better — play now
      tryPlay();
    } else {
      faceVideo.addEventListener('loadeddata', onReady, { once: true });
      faceVideo.addEventListener('canplay', onReady, { once: true });
      faceVideo.addEventListener('canplaythrough', onReady, { once: true });
      // Kick the load in case it stalled
      faceVideo.load();
    }

    // Resume playback when the tab becomes visible again (browsers pause
    // video playback for background tabs).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !disposed) {
        // Brief delay lets the browser fully resume the tab's media pipeline
        setTimeout(() => {
          if (!disposed && faceVideo.paused) {
            tryPlay();
          }
        }, 100);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Safety net: periodically check the video hasn't stalled/paused
    // unexpectedly (e.g. iOS low-power mode, race after StrictMode remount).
    const watchdog = setInterval(() => {
      if (!disposed && faceVideo.paused && document.visibilityState === 'visible') {
        tryPlay();
      }
    }, 2000);

    return () => {
      disposed = true;
      clearInterval(watchdog);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      faceVideo.removeEventListener('loadeddata', onReady);
      faceVideo.removeEventListener('canplay', onReady);
      faceVideo.removeEventListener('canplaythrough', onReady);
      if (interactListener) {
        document.removeEventListener('pointerdown', interactListener);
        document.removeEventListener('touchstart', interactListener);
      }
      faceVideo.pause();
    };
  }, [faceVideo, faceTexture]);

  useEffect(() => {
    if (!scene) {
      return;
    }

    const faceMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: faceTexture,
      blending: THREE.AdditiveBlending,
      transparent: false,
      premultipliedAlpha: true,
      depthWrite: true,
      depthTest: true,
      side: THREE.FrontSide,
    });
    faceMaterialRef.current = faceMaterial;

    scene.traverse((child) => {
      if (child.isSkinnedMesh || child.isMesh) {
        if (child.name === 'faceMesh' || child.material?.name === 'FaceMesh') {
          child.material = faceMaterial;
          child.renderOrder = 2;
        }
        if (child.name === 'Hat') {
          if (child.material) {
            child.material.transparent = true;
            child.material.depthWrite = true;
            child.material.depthTest = true;
          }
          child.renderOrder = 3;
        }
        child.castShadow = false;
        child.receiveShadow = false;
      }
    });

    const namedBone =
      scene.getObjectByName('bone1') ||
      scene.getObjectByName('Bone1') ||
      scene.getObjectByName('Head') ||
      scene.getObjectByName('head');

    if (namedBone && namedBone.isBone) {
      boneRef.current = namedBone;
      return;
    }

    let firstBone = null;
    scene.traverse((child) => {
      if (!firstBone && child.isBone) {
        firstBone = child;
      }
    });
    boneRef.current = firstBone;

    return () => {
      // Clean up face material
      if (faceMaterialRef.current) {
        faceMaterialRef.current.dispose();
        faceMaterialRef.current = null;
      }
    };
  }, [scene, faceTexture]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!isDragging || !boneRef.current) {
        return;
      }
      const deltaX = (event.clientX - lastPoint.current.x) / 200;
      const deltaY = (event.clientY - lastPoint.current.y) / 200;

      const minRotX = THREE.MathUtils.degToRad(-25);
      const maxRotX = THREE.MathUtils.degToRad(5);
      const minRotY = THREE.MathUtils.degToRad(-25);
      const maxRotY = THREE.MathUtils.degToRad(35);

      boneRef.current.rotation.y = THREE.MathUtils.clamp(
        boneRef.current.rotation.y + deltaX,
        minRotY,
        maxRotY
      );
      boneRef.current.rotation.x = THREE.MathUtils.clamp(
        boneRef.current.rotation.x + deltaY,
        minRotX,
        maxRotX
      );

      lastPoint.current = { x: event.clientX, y: event.clientY };
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging]);

  useEffect(() => {
    const minRotX = THREE.MathUtils.degToRad(-25);
    const maxRotX = THREE.MathUtils.degToRad(5);
    const minRotY = THREE.MathUtils.degToRad(-25);
    const maxRotY = THREE.MathUtils.degToRad(35);

    const scheduleNext = () => {
      if (!boneRef.current) return;
      const current = boneRef.current.rotation;

      autoAnimRef.current.startRotation.set(current.x, current.y, current.z);
      autoAnimRef.current.targetRotation.set(
        THREE.MathUtils.randFloat(minRotX, maxRotX),
        THREE.MathUtils.randFloat(minRotY, maxRotY),
        0
      );
      autoAnimRef.current.duration = THREE.MathUtils.randFloat(1500, 3500);
      autoAnimRef.current.startTime = performance.now();
      autoAnimRef.current.isAnimating = true;
    };

    const tick = () => {
      if (!isDragging && boneRef.current && autoAnimRef.current.isAnimating) {
        const now = performance.now();
        const { startTime, duration, startRotation, targetRotation } = autoAnimRef.current;
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = -(Math.cos(Math.PI * progress) - 1) / 2;

        boneRef.current.rotation.x = THREE.MathUtils.lerp(startRotation.x, targetRotation.x, eased);
        boneRef.current.rotation.y = THREE.MathUtils.lerp(startRotation.y, targetRotation.y, eased);
        boneRef.current.rotation.z = THREE.MathUtils.lerp(startRotation.z, targetRotation.z, eased);

        // Immediately chain the next movement when this one finishes
        if (progress >= 1) {
          scheduleNext();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    // Kick off the first movement
    scheduleNext();
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isDragging]);

  return (
    <>
      <color attach="background" args={['rgb(14, 16, 17)']} />
      <Environment files={HDR_PATH} background={false} environmentIntensity={0.35} />
      <primitive
        object={scene}
        scale={1}
        position={[-4, -4, -4]}
        onPointerDown={(event) => {
          event.stopPropagation();
          setIsDragging(true);
          autoAnimRef.current.isAnimating = false;
          lastPoint.current = { x: event.clientX, y: event.clientY };
          trackFirstInteraction();
          if (event.nativeEvent?.target && event.pointerId !== undefined) {
            event.nativeEvent.target.setPointerCapture(event.pointerId);
          }
        }}
        onPointerUp={(event) => {
          setIsDragging(false);
          if (event.nativeEvent?.target && event.pointerId !== undefined) {
            event.nativeEvent.target.releasePointerCapture(event.pointerId);
          }
        }}
      />
      <EffectComposer multisampling={0}>
        <Bloom intensity={2} luminanceThreshold={0.6} luminanceSmoothing={0.9} />
        <LUT lut={lut} />
        
      </EffectComposer>
    </>
  );
};

useGLTF.preload(MODEL_PATH);

export default ARHeadScene;
